import { Dropdown, Form } from "react-bootstrap";
import CachedIcon from "mdi-react/CachedIcon";
import { useQuery } from "react-query";
import currency from "currency.js";
import {
  format,
  parse,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import queryString from "query-string";
import { CSVLink } from "react-csv";
import Select from "react-select";
import { ExcelIcon, PDFIcon } from "../Icons";
import "./../../assets/scss/reports/cashbook.scss";
import useDebounce, {
  useCurrencies,
  useQueryParams,
  useScrollTop,
} from "../../utils/hooks";
import { useAuth } from "../../hooks/useAuth";
import { useDownloadExcel } from "../../hooks/useDownloadExcel";
import ModalLoader from "../utils/ModalLoader";
import RsDateRangePicker from "../utils/RsDateRangePicker";
import { IsPrivileged } from "../DisplayChildElement";
import { useNavigate } from "react-router-dom";
import { formatDate, getMonthLabel, months } from "../../utils/helpers";
import { addDays } from "date-fns";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import MultiDateRangerPicker from "../utils/MultiDateRangerPicker";

function BalanceSheet2() {
  useScrollTop();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const initialFilterParams = {
    startDate: "",
    endDate: "",
    dateRanges: `${format(startOfYear(new Date()), "yyyy-MM-dd")}|${format(
      endOfYear(new Date()),
      "yyyy-MM-dd"
    )}|Year|${currentYear}`,
  };

  const { backendUrl } = useAuth();
  const [excelData, setExcelData] = useState([]);
  const {
    deploymentCurrencies: currenciesOptions,
    prevailingCurrency,
    getCurrencySymbol,
  } = useCurrencies();
  const CSVLinkRef = useRef(null);

  const [isfetchingExcel, fetchExcelData] = useDownloadExcel(
    excelData,
    CSVLinkRef
  );

  const [queryParams, setQueryParams] = useQueryParams({
    ...initialFilterParams,
    currency: "",
    convertCurrency: prevailingCurrency,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);
  useEffect(() => {
    setQueryParams({
      ...queryParams,
      ...debouncedFilterParams,
    });
  }, [debouncedFilterParams]);

  const fetchCustomerLedgers = async queryParams => {
    let response = await fetch(
      `${backendUrl}/api/reports/balance-sheet-2?&${queryString.stringify(
        queryParams
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    return data;
  };

  const {
    data = {
      results: [],
      company: {},
    },
    refetch,
    isFetching,
  } = useQuery(
    ["TrailBalance", queryParams],
    () => fetchCustomerLedgers(queryParams),
    {
      keepPreviousData: true,
    }
  );

  const results = data?.results || [];

  const fmt = (amount, currencyCode) =>
    currency(amount || 0, {
      symbol: getCurrencySymbol(currencyCode || filterParams.convertCurrency),
    }).format();

  const sumBalances = children => {
    if (!children || children.length === 0) return currency(0);
    const total = children
      .filter(c => !c.isTotal)
      .reduce((acc, c) => {
        if (c.children && c.children.length) {
          return acc.add(sumBalances(c.children));
        }
        return acc.add(Number(c.balance || 0));
      }, currency(0));
    return total;
  };

  const [collapsedNodes, setCollapsedNodes] = useState({});
  const toggleNode = key =>
    setCollapsedNodes(prev => ({ ...prev, [key]: !prev[key] }));

  const { limit, page, ...rest } = queryParams;
  const onDownloadExcelData = async () => {
    let exData;

    const date =
      rest.startDate && rest.endDate
        ? `Date Prepared: Between ${format(
            new Date(rest.startDate),
            "E MMM d yyyy k:mm:ss z"
          )} to ${format(new Date(rest.endDate), "E MMM d yyyy k:mm:ss z")}`
        : "";

    exData = [[data.company?.CompName], ["Balance Sheet Report"], [date], [""]];

    results.forEach(result => {
      const accounts = result.fininacialAccounts || [];

      accounts.forEach(group => {
        exData.push([group.accountType]);
        exData.push([group.subType]);
        (group.children || []).forEach(child => {
          if (child.isTotal) {
            exData.push([child.accountClassification, fmt(child.balance)]);
          } else {
            exData.push([child.accountClassification, fmt(child.balance)]);
            if (child.children && child.children.length) {
              child.children.forEach(sub => {
                exData.push([
                  `  ${sub.accountClassification}`,
                  fmt(sub.balance),
                ]);
              });
            }
          }
        });
        exData.push([""]);
      });
    });

    setExcelData(exData);
  };

  const filterByDateRange = date => {
    setFilterParams({
      ...filterParams,
      startDate: format(date[0], "yyyy-MM-dd"),
      endDate: format(date[1], "yyyy-MM-dd"),
      enableDateRange: true,
    });
  };

  const clearDateRange = () => {
    setFilterParams({
      ...filterParams,
      startDate: "",
      endDate: "",
    });
  };

  const currencySymbol = useMemo(() => {
    return getCurrencySymbol(
      filterParams.convertCurrency
        ? filterParams.convertCurrency
        : prevailingCurrency
    );
  }, [filterParams.convertCurrency, prevailingCurrency]);

  const viewAccount = ({ e, child }) => {
    if (e.detail === 2) {
      const desc = child?.accountClassification || "";
      navigate(
        `/account-setup/chart-of-account-report?Description=${encodeURIComponent(
          desc
        )}&currency=${filterParams.currency}&convertCurrency=${
          filterParams.convertCurrency
        }&startDate=${filterParams.startDate ||
          ""}&endDate=${filterParams.endDate || ""}`
      );
    }
  };

  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  // Get all unique account classifications across all periods
  const getAllAccountClassifications = accountType => {
    const classifications = new Set();

    results.forEach(result => {
      const accounts = result.fininacialAccounts || [];
      const groups = accounts.filter(
        a => (a.accountType || "").toLowerCase() === accountType.toLowerCase()
      );

      groups.forEach(grp => {
        (grp.children || []).forEach(child => {
          if (!child.isTotal) {
            classifications.add(child.accountClassification);
            if (child.children && child.children.length) {
              child.children.forEach(sub => {
                classifications.add(sub.accountClassification);
              });
            }
          }
        });
      });
    });

    return Array.from(classifications);
  };

  // Get balance for a specific classification in a specific result
  const getBalance = (
    result,
    accountType,
    classification,
    isSubAccount = false
  ) => {
    const accounts = result.fininacialAccounts || [];
    const groups = accounts.filter(
      a => (a.accountType || "").toLowerCase() === accountType.toLowerCase()
    );

    for (const grp of groups) {
      for (const child of grp.children || []) {
        if (!child.isTotal) {
          if (child.accountClassification === classification && !isSubAccount) {
            return child.balance;
          }
          if (child.children && child.children.length) {
            for (const sub of child.children) {
              if (sub.accountClassification === classification) {
                return sub.balance;
              }
            }
          }
        }
      }
    }

    return 0;
  };

  // Get total for account type
  const getTotal = (result, accountType) => {
    const accounts = result.fininacialAccounts || [];
    const groups = accounts.filter(
      a => (a.accountType || "").toLowerCase() === accountType.toLowerCase()
    );

    let total = currency(0);
    groups.forEach(grp => {
      const children = grp.children || [];
      const totalChild = children.find(c => c.isTotal);
      if (totalChild) {
        total = total.add(Number(totalChild.balance || 0));
      } else {
        total = total.add(sumBalances(children));
      }
    });

    return total.value;
  };

  const renderMultiColumnSection = (accountType, title) => {
    const allClassifications = getAllAccountClassifications(accountType);

    if (allClassifications.length === 0) return null;

    // Build parent -> children map per subType (preserve insertion order)
    const classificationsBySubType = {};
    results.forEach(result => {
      const accounts = result.fininacialAccounts || [];
      const groups = accounts.filter(
        a => (a.accountType || "").toLowerCase() === accountType.toLowerCase()
      );

      groups.forEach(grp => {
        if (!classificationsBySubType[grp.subType]) {
          classificationsBySubType[grp.subType] = new Map(); // parent -> Set(children)
        }
        const parentMap = classificationsBySubType[grp.subType];

        (grp.children || []).forEach(child => {
          if (child.isTotal) return;
          // ensure parent entry exists
          if (!parentMap.has(child.accountClassification)) {
            parentMap.set(child.accountClassification, new Set());
          }
          // if child has sub-children, add them under this parent
          if (child.children && child.children.length) {
            child.children.forEach(sub => {
              parentMap
                .get(child.accountClassification)
                .add(sub.accountClassification);
            });
          }
        });
      });
    });

    const subTypes = Object.keys(classificationsBySubType);
    const columnWidth = `${100 / (results.length + 1)}%`;

    return (
      <div className="mb-5">
        <div className="fw-bold p-3 px-3 mb-3 bg-low-blue text-white">
          <p className="mb-0">{title}</p>
        </div>

        {subTypes.map(subType => (
          <div key={subType} className="mb-4">
            <div className="table-responsive" style={{ maxHeight: "unset" }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th
                      className="bg-light text-white"
                      style={{ width: columnWidth }}
                    >
                      {subType}
                    </th>
                    {results.map((result, idx) => (
                      <th
                        key={idx}
                        className="text-end bg-light text-white"
                        style={{ width: columnWidth }}
                      >
                        {getMonthLabel(result.dateRange?.month)}{" "}
                        {result.dateRange?.year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from(classificationsBySubType[subType].entries()).map(
                    ([parentClassification, childrenSet], idx) => {
                      const hasChildren = childrenSet && childrenSet.size > 0;
                      const keyBase = `${accountType}_${subType}_${parentClassification}`;

                      // expanded state stored in collapsedNodes (truthy => expanded)
                      const isExpanded = !!collapsedNodes[keyBase];

                      return (
                        <React.Fragment key={idx}>
                          <tr>
                            <td
                              style={{
                                paddingLeft: "1rem",
                                cursor: hasChildren ? "pointer" : "default",
                                userSelect: "none",
                                width: columnWidth,
                              }}
                              onClick={() => hasChildren && toggleNode(keyBase)}
                            >
                              {hasChildren ? (
                                <span style={{ marginRight: 8 }}>
                                  {isExpanded ? "▼" : "▶"}
                                </span>
                              ) : null}
                              {parentClassification}
                            </td>
                            {results.map((result, resultIdx) => {
                              const balance = getBalance(
                                result,
                                accountType,
                                parentClassification,
                                false
                              );
                              return (
                                <td
                                  key={resultIdx}
                                  className="text-end"
                                  style={{ width: columnWidth }}
                                >
                                  {fmt(balance)}
                                </td>
                              );
                            })}
                          </tr>

                          {/* render children if any and expanded */}
                          {hasChildren && isExpanded
                            ? Array.from(childrenSet).map(
                                (childClass, cIdx) => (
                                  <tr key={`${idx}-child-${cIdx}`}>
                                    <td
                                      style={{
                                        paddingLeft: "2rem",
                                        width: columnWidth,
                                      }}
                                    >
                                      {childClass}
                                    </td>
                                    {results.map((result, resultIdx) => {
                                      const childBalance = getBalance(
                                        result,
                                        accountType,
                                        childClass,
                                        true
                                      );
                                      return (
                                        <td
                                          key={resultIdx}
                                          className="text-end"
                                          style={{ width: columnWidth }}
                                        >
                                          {fmt(childBalance)}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                )
                              )
                            : null}
                        </React.Fragment>
                      );
                    }
                  )}

                  {/* Subtotal row */}
                  <tr className="fw-bold border-top-2">
                    <td style={{ width: columnWidth }}>Total {subType}</td>
                    {results.map((result, idx) => {
                      const accounts = result.fininacialAccounts || [];
                      const groups = accounts.filter(
                        a =>
                          (a.accountType || "").toLowerCase() ===
                          accountType.toLowerCase()
                      );
                      const subTypeGroup = groups.find(
                        g => g.subType === subType
                      );
                      const totalChild = (subTypeGroup?.children || []).find(
                        c => c.isTotal
                      );
                      const total = totalChild ? totalChild.balance : 0;

                      return (
                        <td
                          key={idx}
                          className="text-end"
                          style={{ width: columnWidth }}
                        >
                          {fmt(total)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* Grand total row */}
        <div className="table-responsive" style={{ maxHeight: "unset" }}>
          <table className="table table-bordered">
            <tbody>
              <tr className="fw-bold border-top-2">
                <td style={{ width: columnWidth }}>Total {title}</td>
                {results.map((result, idx) => {
                  const total = getTotal(result, accountType);
                  return (
                    <td
                      key={idx}
                      className="text-end"
                      style={{ width: columnWidth }}
                    >
                      {fmt(total)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <IsPrivileged roleName="Financials">
      <main className="cash-book">
        <div className="content pb-5">
          <header className="container pt-5">
            <h1>
              Balance Sheet
              <button
                title="Refresh"
                onClick={() => refetch()}
                className="btn text-primary"
              >
                <CachedIcon />
              </button>
            </h1>
            <div className="actions gap-3">
              <CSVLink
                className="btn print d-none"
                filename={`Balance Sheet(${format(
                  new Date(),
                  "dd-MMM-yyyy hh:mm:ss a"
                )}).csv`}
                data={excelData}
                ref={CSVLinkRef}
              />

              <Form.Group className="position-relative">
                <Form.Label className="fw-5 position-absolute bottom-100 start-0 m-1">
                  Filter By
                </Form.Label>
                <Select
                  classNamePrefix="form-select"
                  placeholder="All Currencies"
                  isSearchable={true}
                  options={currenciesOptions}
                  value={currenciesOptions.find(
                    el => el.value == filterParams.currency
                  )}
                  onChange={selected =>
                    setFilterParams({
                      ...filterParams,
                      page: 1,
                      currency: selected?.value,
                    })
                  }
                  isClearable={true}
                />
              </Form.Group>

              <Form.Group className="position-relative">
                <Form.Label className="fw-5 position-absolute bottom-100 start-0 m-1">
                  Report In{" "}
                </Form.Label>
                <Select
                  classNamePrefix="form-select"
                  placeholder="Select Currency"
                  isSearchable={true}
                  options={currenciesOptions}
                  value={currenciesOptions.find(
                    el => el.value === filterParams.convertCurrency
                  )}
                  onChange={({ value }) =>
                    setFilterParams({
                      ...filterParams,
                      page: 1,
                      convertCurrency: value,
                    })
                  }
                />
              </Form.Group>

              <button
                className="btn print"
                onClick={() => {
                  onDownloadExcelData();
                  fetchExcelData();
                }}
              >
                Excel <ExcelIcon color="#008000" />
              </button>
            </div>
          </header>

          <div className="container pb-5">
            <MultiDateRangerPicker
              filterParams={filterParams}
              setFilterParams={setFilterParams}
            />
          </div>

          <div className="container mb-5">
            {/* Render ASSETS */}
            {renderMultiColumnSection("ASSETS", "ASSETS")}

            {/* Render LIABILITIES */}
            <div className="mt-5">
              <div className="fw-bold p-3 px-3 mb-3 bg-low-blue text-white">
                <p className="mb-0">LIABILITIES AND OWNER'S EQUITY</p>
              </div>

              {renderMultiColumnSection("LIABILITIES", "LIABILITIES")}
              {renderMultiColumnSection("EQUITY", "EQUITY")}
            </div>
          </div>
        </div>
        <ModalLoader show={isfetchingExcel} />
      </main>
    </IsPrivileged>
  );
}

export default BalanceSheet2;
